import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

const navItems = [
  { label: "首页", to: "/" },
  { label: "电影", to: "/category?type=movie" },
  { label: "电视剧", to: "/category?type=tv" },
  { label: "搜索", to: "/search" },
  { label: "关于", to: "/about" },
];

interface NavbarProps {
  overlay?: boolean;
}

export default function Navbar({ overlay = false }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  const solid = !overlay || scrolled || menuOpen;

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/search?q=${encodeURIComponent(value)}` : "/search");
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        solid
          ? "bg-background-950/95 backdrop-blur-md border-b border-background-300"
          : "bg-gradient-to-b from-foreground-50/80 via-foreground-50/40 to-transparent border-b border-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-8 h-16 md:h-[72px] flex items-center gap-6">
        <Link to="/" className="flex items-center gap-2 shrink-0 group">
          <span className="w-9 h-9 rounded-md bg-primary-500 flex items-center justify-center text-white text-lg">
            <i className="ri-film-line" />
          </span>
          <span className="flex flex-col leading-none">
            <span className="text-[17px] font-bold tracking-wide text-foreground-950">
              嫩草影院
            </span>
            <span className="hidden sm:block text-[10px] tracking-[0.28em] text-foreground-500 mt-1">
              NEVCAO CINEMA
            </span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1 flex-1 justify-center">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `px-4 py-2 text-sm rounded-md transition-colors whitespace-nowrap ${
                  isActive
                    ? "text-foreground-950 bg-background-300/70"
                    : "text-foreground-700 hover:text-foreground-950 hover:bg-background-300/50"
                }`
              }
              end={item.to === "/"}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <form onSubmit={handleSearch} className="hidden lg:flex items-center gap-2 w-64">
          <div className="relative w-full">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 text-sm" />
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索影片、剧集"
              className="w-full h-9 pl-9 pr-3 rounded-full bg-background-200/80 border border-background-400 text-sm text-foreground-900 placeholder:text-foreground-500 outline-none focus:border-primary-500 transition-colors"
            />
          </div>
        </form>

        <div className="hidden lg:flex items-center shrink-0">
          <Link
            to="/category"
            className="h-9 px-4 inline-flex items-center gap-2 rounded-full bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium transition-colors whitespace-nowrap"
          >
            <i className="ri-play-circle-line text-base" />
            开始观影
          </Link>
        </div>

        <button
          type="button"
          aria-label="打开导航菜单"
          onClick={() => setMenuOpen((prev) => !prev)}
          className="md:hidden ml-auto w-10 h-10 flex items-center justify-center rounded-md bg-background-200/80 border border-background-400 text-foreground-900 cursor-pointer"
        >
          <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-xl`} />
        </button>
      </div>

      {menuOpen && (
        <div className="md:hidden px-4 pb-5 pt-2 bg-background-950/95 border-t border-background-300 animate-fade-in">
          <nav className="flex flex-col gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `px-3 py-3 rounded-md text-[15px] transition-colors ${
                    isActive
                      ? "bg-primary-500 text-white"
                      : "text-foreground-800 hover:bg-background-200"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
          <form onSubmit={handleSearch} className="mt-3 relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 text-sm" />
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索影片、剧集"
              className="w-full h-11 pl-9 pr-3 rounded-md bg-background-200 border border-background-400 text-sm text-foreground-900 placeholder:text-foreground-500 outline-none focus:border-primary-500"
            />
          </form>
        </div>
      )}
    </header>
  );
}